package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.NewsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.DealsRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.FirebaseServices
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.NewsArticle
import diana.soleil.design.iraniancanadienbazzar.core.model.Deal
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.withTimeout
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.map

/**
 * Editorial + merchant content: community news and deals/promotions.
 *
 * Split out of the former 2,753-line FirestoreRepositories.kt (2026-08-02) — same code,
 * same behaviour, one file per domain. Shared collection names and helpers live in
 * FirestoreCommon.kt.
 */

/**
 * Live [NewsRepository] over the `news` collection. Server filters visibility only (single-field
 * query, no composite index); newest-first ordering and the metro gate (national `metroId == null`
 * OR active metro) happen client-side over the small capped set — same pattern as other streams.
 */
class FirestoreNewsRepository(
    firebase: FirebaseServices,
) : NewsRepository {
    private val firestore: FirebaseFirestore = firebase.firestore

    override fun observeNews(metroId: String?, limit: Int): Flow<DataResult<List<NewsArticle>>> =
        firestore.collection(NEWS).where { "visible" equalTo true }.limit(50).snapshots
            .map { snap ->
                val articles = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<NewsDoc>().toModel(doc.id) }.getOrNull() }
                    .filter { it.metroId == null || metroId == null || it.metroId == metroId }
                    .sortedByDescending { it.publishedAtMillis }
                    .take(limit)
                DataResult.Success(articles) as DataResult<List<NewsArticle>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun getArticle(articleId: String): DataResult<NewsArticle> = try {
        val snapshot = firestore.collection(NEWS).document(articleId).get()
        val article = if (snapshot.exists) snapshot.decode<NewsDoc>().toModel(snapshot.id) else null
        if (article == null) DataResult.Failure(AppError.NotFound()) else DataResult.Success(article)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }
}

/** Merchant deals/promotions over `deals/{id}` (see [DealsRepository]). */
class FirestoreDealsRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : DealsRepository {
    private val firestore: FirebaseFirestore = firebase.firestore

    override fun observeActiveDeals(limit: Int): Flow<DataResult<List<Deal>>> {
        val now = Timestamp.now()
        return firestore.collection(DEALS)
            .where { "visible" equalTo true }
            .where { "endsAt" greaterThan now }
            .orderBy("endsAt", Direction.ASCENDING)
            .limit(limit)
            .snapshots
            .map { snap ->
                val deals = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<DealDoc>().toModel(doc.id) }.getOrNull()
                }
                    // A deal with no title in any language renders as a blank card in the Home rail
                    // (and formatted to a bare " — " in the activity ticker, which is how this was
                    // spotted on dev web). Skip it here — in PUBLIC discovery only. The owner-facing
                    // observeDealsForBusiness deliberately does NOT filter: hiding a merchant's own
                    // broken deal from their dashboard would leave them no way to fix or delete it.
                    .filter { !it.title.isEmpty }
                DataResult.Success(deals) as DataResult<List<Deal>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }
    }

    override fun observeDealsForBusiness(businessId: String): Flow<DataResult<List<Deal>>> =
        firestore.collection(DEALS)
            .where { "businessId" equalTo businessId }
            .snapshots
            .map { snap ->
                val deals = snap.documents.mapNotNull { doc ->
                    runCatching { doc.decode<DealDoc>().toModel(doc.id) }.getOrNull()
                }.sortedByDescending { it.endsAtMillis }
                DataResult.Success(deals) as DataResult<List<Deal>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun createDeal(
        businessId: String,
        businessName: String,
        title: LocalizedText,
        details: LocalizedText?,
        endsAtMillis: Long,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            withTimeout(WRITE_TIMEOUT_MS) {
                firestore.collection(DEALS).add(
                    DealCreateDoc(
                        businessId = businessId,
                        businessName = businessName,
                        title = LocalizedTextDoc(en = title.en, fr = title.fr, fa = title.fa),
                        details = details?.let { LocalizedTextDoc(en = it.en, fr = it.fr, fa = it.fa) },
                        endsAt = Timestamp(endsAtMillis / 1_000, ((endsAtMillis % 1_000) * 1_000_000).toInt()),
                        visible = true,
                        createdAt = Timestamp.ServerTimestamp,
                    ),
                )
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun deleteDeal(dealId: String): DataResult<Unit> = try {
        firestore.collection(DEALS).document(dealId).delete()
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }
}
